
import { Injectable } from '@angular/core';

@Injectable({
    providedIn: 'root'
})
export class PublicFunctionsService {


    constructor() { }

    getItemById(arr, id) {
        return arr.find(item => item.id == id)
    }

    formatNumber(value: number): string {
        return value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    }

    /**
     * en-US grouping. Fractional part is rounded up (toward +∞) to exactly `fractionDigits` places, then zero-padded.
     */
    formatNumberCeilFractions(value: number, fractionDigits: number): string {
        if (!Number.isFinite(value)) {
            return String(value);
        }
        const factor = 10 ** fractionDigits;
        const roundedUp = Math.ceil(value * factor) / factor;
        return roundedUp.toLocaleString('en-US', {
            minimumFractionDigits: fractionDigits,
            maximumFractionDigits: fractionDigits,
            useGrouping: true,
        });
    }

    /**
     * KPI / SQL preview cells: formats finite numbers, bigint, and API numeric strings
     * (decimals, integers, scientific) with {@link formatNumberCeilFractions}.
     * Non-numeric strings (labels, codes, padded IDs like "007") are returned unchanged.
     */
    formatPreviewNumericCell(value: unknown, fractionDigits: number = 4): string {
        if (value === null || value === undefined) {
            return '';
        }
        if (typeof value === 'number') {
            return Number.isFinite(value)
                ? this.formatNumberCeilFractions(value, fractionDigits)
                : String(value);
        }
        if (typeof value === 'bigint') {
            return this.formatNumberCeilFractions(Number(value), fractionDigits);
        }
        if (typeof value === 'string') {
            const n = this.tryParseNumericApiString(value);
            return n !== null
                ? this.formatNumberCeilFractions(n, fractionDigits)
                : value;
        }
        return String(value);
    }

    private tryParseNumericApiString(s: string): number | null {
        const t = s.trim();
        if (!t) {
            return null;
        }
        if (!this.isNumericApiString(t)) {
            return null;
        }
        const n = Number(t);
        return Number.isFinite(n) ? n : null;
    }

    /**
     * Accepts typical JSON/API number strings; rejects padded integer strings (e.g. "007", "000")
     * so codes are not shown as formatted numbers.
     */
    private isNumericApiString(t: string): boolean {
        if (!/^[-+]?(?:\d+\.\d*|\d*\.\d+|\d+)(?:[eE][-+]?\d+)?$/.test(t)) {
            return false;
        }
        if (/^[-+]?\d+$/.test(t)) {
            const body = t.replace(/^[-+]/, '');
            if (body.length > 1 && body.startsWith('0')) {
                return false;
            }
        }
        return true;
    }

    formatDate = (input: string | Date | { toJSDate: () => Date }): string => {
        const date = this.toNativeDate(input);
        if (!date || isNaN(date.getTime())) {
            return 'Invalid Date';
        }
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
    };

    private toNativeDate(input: string | Date | { toJSDate: () => Date }): Date | null {
        if (typeof input === 'string') {
            return new Date(input);
        }
        if (input instanceof Date) {
            return input;
        }
        if (typeof input === 'object' && input !== null && 'toJSDate' in input) {
            const jsDate = input.toJSDate();
            return jsDate instanceof Date ? jsDate : null;
        }
        return null;
    }

}
